import { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from 'common';

import NavBar, { View } from '../../../components/NavBar';
import { Alert } from '@vivaahealth/design-system';

import { Content } from './content';
import { withFetchPatient } from '../../../components/hoc/withFetchPatient';
import {
  getFieldsBySection,
  REVIEW_SECTIONS,
  SectionType,
} from '../../../utils/review';
import { useForm } from '../../../hooks/form/useForm';
import { useAsyncFlow } from '../../../hooks/common/useAsyncFlow';
import { validateFields } from '../../../utils/form/field';
import { getReviewPath } from '../../../utils/form';
import { getSanitizedErrorMessage } from '../../../utils/error';

import appStyles from '../../App.module.css';
import formStyles from '../../FormStart/index.module.css';

const EditContainer = () => {
  const { authenticationCode, formId, formSetId, sectionType } = useParams<{
    authenticationCode: string;
    formId: string;
    formSetId: string;
    sectionType: SectionType;
  }>();
  const history = useHistory();
  const { t } = useTranslation('review');
  const {
    form,
    formSets,
    patient,
    formSet,
    saveFieldValue,
    setFieldValue,
    multiFormSet,
    visibleFormFields,
  } = useForm({
    authenticationCode,
    formId,
  });

  const displayFields = getFieldsBySection(
    visibleFormFields,
    sectionType,
    REVIEW_SECTIONS,
  );

  const {
    execute: validateAndSavePageFields,
    pending,
    error,
  } = useAsyncFlow(
    async (fields: api.PrevisitFormField.PrevisitFormFieldWithConfig[]) =>
      validateFields({
        formSetId,
        fields: fields,
        formFields: form.fields,
        practicePreference: formSet.practicePreference,
        patient,
        save: true,
      }),
  );

  useEffect(() => {
    if (!error) window.scrollTo(0, 0);
  }, [error]);

  const displayError = getSanitizedErrorMessage(error);

  return (
    <div className={appStyles.App}>
      <NavBar view={View.Form} />
      <div className={formStyles.FormContainer}>
        <div className={formStyles.Form}>
          {displayError && (
            <Alert variant="danger" className={'mb-4'}>
              {displayError}
            </Alert>
          )}
          <Content
            initialFields={displayFields}
            formFields={form.fields}
            formSets={formSets}
            patient={patient}
            practicePreference={formSet.practicePreference}
            encounter={formSet.encounter}
            saveFieldValue={saveFieldValue}
            setFieldValue={setFieldValue}
            selectedFormSetId={formSetId}
            saveButtonLabel={t('common::saveUpdates')}
            navigateToNextPage={async () =>
              history.push(
                getReviewPath({
                  authenticationCode,
                  formSetId,
                  formId,
                  multi: multiFormSet,
                }),
              )
            }
            validateAndSavePageFields={validateAndSavePageFields}
            pending={pending}
          />
        </div>
      </div>
    </div>
  );
};

export const ReviewEditPage = withFetchPatient(EditContainer);
export const ReviewEditPageMulti = withFetchPatient(EditContainer, {
  multi: true,
});
